import { useState } from "react";
import { MapPin, Check, X, Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export const PincodeChecker = ({ compact = false, onResult }) => {
  const [pincode, setPincode] = useState("");
  const [status, setStatus] = useState("idle");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const check = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    setResult(null);
    try {
      const { data } = await api.get("/serviceability/check", { params: { pincode } });
      setResult(data);
      setStatus("done");
      onResult?.(data);
    } catch (err) {
      setStatus("error");
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="pincode-checker" className={compact ? "" : "mbm-card p-6 sm:p-8"}>
      {!compact && (
        <>
          <h3 className="font-heading text-lg font-semibold">Check your area</h3>
          <p className="mt-2 text-sm text-cocoa/80">
            Enter your Pune pincode to see whether we deliver to you.
          </p>
        </>
      )}
      <form onSubmit={check} className="mt-4 flex flex-col gap-3 sm:flex-row">
        <label htmlFor="pincode-input" className="sr-only">
          Pincode
        </label>
        <div className="relative flex-1">
          <MapPin
            className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-coralInk"
            aria-hidden="true"
          />
          <input
            id="pincode-input"
            data-testid="pincode-input"
            inputMode="numeric"
            pattern="[0-9]{6}"
            maxLength={6}
            required
            value={pincode}
            onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
            placeholder="411001"
            className="min-h-[48px] w-full rounded-control bg-cream border border-line pl-10 pr-4 text-sm focus:border-forest focus:outline-none focus:ring-2 focus:ring-forest/25"
          />
        </div>
        <button
          type="submit"
          data-testid="pincode-check-button"
          disabled={status === "loading"}
          className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-control bg-coralInk px-6 font-heading text-sm font-semibold text-white transition-colors hover:bg-forest disabled:opacity-60"
        >
          {status === "loading" && <Loader2 className="h-4 w-4 animate-spin" />}
          Check availability
        </button>
      </form>

      {status === "done" && result && (
        <div
          data-testid="pincode-result"
          className="mt-4 flex gap-3 rounded-[14px] border p-4"
          style={{
            borderColor: "var(--brand-cocoa)",
            background: result.serviceable ? "var(--brand-sage-soft)" : "var(--brand-forest)",
            color: result.serviceable ? "var(--brand-cocoa)" : "var(--brand-cream)",
          }}
        >
          {result.serviceable ? (
            <Check className="mt-0.5 h-5 w-5 shrink-0 text-coralInk" aria-hidden="true" />
          ) : (
            <X className="mt-0.5 h-5 w-5 shrink-0 text-rust" aria-hidden="true" />
          )}
          <div className="text-sm">
            <p className="font-heading font-semibold text-cocoa">{result.message}</p>
            {result.serviceable && (
              <ul className="mt-2 space-y-1 text-[13px] text-cocoa/80">
                <li>Area: {result.area}</li>
                <li>Delivery days: {result.delivery_days}</li>
                <li>Delivery window: {result.delivery_window}</li>
                <li>Order cut-off: {result.cutoff}</li>
                {result.schools?.length > 0 && (
                  <li>School delivery: {result.schools.join(", ")}</li>
                )}
              </ul>
            )}
          </div>
        </div>
      )}

      {status === "error" && (
        <p data-testid="pincode-error" className="mt-3 text-sm text-rust">
          {error}
        </p>
      )}
    </div>
  );
};

export default PincodeChecker;
